import {
  FC,
  createContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  PropsWithChildren,
  memo,
} from 'react';
import { Message, MessageCode } from 'src/common/models/message';
import useWebSocket from 'react-use-websocket';
import { purge } from 'src/lib/purge';
import { WebSocketHook } from 'react-use-websocket/dist/lib/types';
import { toast } from 'react-toastify';
import { ScreenScrapeReconnectToast } from 'src/pages/AspspSelectPage/ScreenScrape/common/ScreenScrapeReconnectToast';
import { toastOptions } from 'src/common/components/Toast/config';
import { Jsonify } from 'type-fest/source/jsonify';
import * as Sentry from '@sentry/browser';
import { useVerificationId } from 'src/common/hooks/useVerificationId';

interface ScreenScrapeContext {
  lastJsonMessage: Jsonify<Message> | null;
  submitStep: (value?: string | null, object?: unknown) => void;
  cancelProcess: () => void;
  isLoading?: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isFirstMessageReceived: boolean;
  isUserReturningToProcess?: boolean;
  connectionLost: boolean;
  closeModal: () => void;
}

export const ScreenScrapeCtx = createContext<ScreenScrapeContext | null>(null);

interface TypedWebsocketHook extends Omit<WebSocketHook, 'lastJsonMessage'> {
  lastJsonMessage: Jsonify<Message> | null;
}

export interface ScreenScrapeProviderProps extends PropsWithChildren {
  wsUrl: string;
  isUserReturningToProcess?: boolean;
  closeModal: () => void;
}

export const ScreenScrapeProvider: FC<ScreenScrapeProviderProps> = memo(
  ({ wsUrl, isUserReturningToProcess, closeModal, children }) => {
    const verificationId = useVerificationId();

    const [isLoading, setIsLoading] = useState(false);
    const [isFirstMessageReceived, setIsFirstMessageReceived] = useState(false);
    const [queuedSubmitData, setQueuedSubmitData] = useState<{
      value?: string | null;
      object?: unknown;
    }>();
    const [connectionLost, setConnectionLost] = useState(false);

    const { sendJsonMessage, lastJsonMessage }: TypedWebsocketHook =
      useWebSocket(wsUrl, {
        onOpen: () => {
          if (connectionLost) {
            sendJsonMessage({ messageCode: MessageCode.GetCurrentMessage });
            setConnectionLost(false);
          }
        },
        share: true,
        shouldReconnect: () => true,
        reconnectInterval: 5000,
        reconnectAttempts: 5,
        onReconnectStop: () => {
          setConnectionLost(true);
          toast(<ScreenScrapeReconnectToast />, {
            ...toastOptions,
            autoClose: false,
          });
        },
        onError: (event) => {
          Sentry.captureMessage(`WS connection error: ${verificationId}`, {
            level: 'error',
            extra: {
              event,
            },
            user: {
              id: verificationId,
            },
          });
        },
      });

    useEffect(() => {
      if (lastJsonMessage) {
        setIsLoading(false);
        if (!isFirstMessageReceived) {
          setIsFirstMessageReceived(true);
          if (queuedSubmitData) {
            submitStep(queuedSubmitData.value, queuedSubmitData.object);
            setQueuedSubmitData(undefined);
          }
        }
      }
    }, [lastJsonMessage]);

    useEffect(() => {
      if (isUserReturningToProcess) {
        setIsLoading(true);
        sendJsonMessage({ messageCode: MessageCode.GetCurrentMessage });
      }
    }, [isUserReturningToProcess]);

    const submitStep = useCallback(
      (value?: string | null, object?: unknown) => {
        setIsLoading(true);

        if (!lastJsonMessage?.messageCode) {
          setQueuedSubmitData({ value, object });
        } else {
          sendJsonMessage(
            purge({ messageCode: lastJsonMessage.messageCode, value, object })
          );
        }
      },
      [lastJsonMessage?.messageCode]
    );

    const cancelProcess = useCallback(() => {
      setIsLoading(true);
      sendJsonMessage({ messageCode: MessageCode.CancelProcess });
    }, []);

    const value = useMemo(
      () => ({
        lastJsonMessage,
        isLoading,
        setIsLoading,
        submitStep,
        isFirstMessageReceived,
        cancelProcess,
        isUserReturningToProcess,
        connectionLost,
        closeModal,
      }),
      [
        lastJsonMessage,
        isLoading,
        submitStep,
        isFirstMessageReceived,
        isUserReturningToProcess,
        connectionLost,
        closeModal,
      ]
    );

    return (
      <ScreenScrapeCtx.Provider value={value}>
        {children}
      </ScreenScrapeCtx.Provider>
    );
  }
);
